/**
 * Message Flow Tracer
 * 
 * Comprehensive tracing system for tracking messages through the entire flow:
 * Frontend → Backend → Database → Backend → Frontend
 * 
 * Usage:
 *   const traceId = MessageTracer.startTrace(content, senderId, receiverId);
 *   MessageTracer.logStep(traceId, 'step-name', { details });
 *   MessageTracer.endTrace(traceId);
 */

interface TraceStep {
  timestamp: number;
  layer: 'Frontend' | 'Backend-WS' | 'Backend-HTTP' | 'Database';
  step: string;
  details: Record<string, any>;
}

interface TraceData {
  traceId: string;
  startTime: number;
  content: string;
  senderId: number;
  receiverId?: number;
  groupId?: number;
  optimisticId?: number;
  messageIds: number[];
  steps: TraceStep[];
  duplicates: number;
  completed: boolean;
}

class MessageTracer {
  private static traces: Map<string, TraceData> = new Map();
  private static enabled: boolean = 
    process.env.REACT_APP_ENABLE_MESSAGE_TRACING === 'true' ||
    process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true';

  /**
   * Start a new trace for a message send attempt
   */
  static startTrace(
    content: string,
    senderId: number,
    receiverId?: number,
    groupId?: number
  ): string {
    if (!this.enabled) {
      return '';
    }

    const traceId = `trace-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const traceData: TraceData = {
      traceId,
      startTime: Date.now(),
      content: content.substring(0, 100), // Store preview only
      senderId,
      receiverId,
      groupId,
      messageIds: [],
      steps: [],
      duplicates: 0,
      completed: false
    };

    this.traces.set(traceId, traceData);

    this.logStep(traceId, 'send-init', {
      content: content.substring(0, 50),
      senderId,
      receiverId,
      groupId
    });

    // Clean up old traces (older than 5 minutes)
    this.cleanup();

    return traceId;
  }

  /**
   * Log a step in the message flow
   */
  static logStep(
    traceId: string,
    step: string,
    details: Record<string, any>,
    layer: 'Frontend' | 'Backend-WS' | 'Backend-HTTP' | 'Database' = 'Frontend'
  ): void {
    if (!this.enabled || !traceId) {
      return;
    }

    const trace = this.traces.get(traceId);
    if (!trace) {
      // Trace not found, but still log for visibility
      console.log(`[${layer}] 🔍 TRACE-${traceId} | STEP: ${step} |`, details);
      return;
    }

    const stepData: TraceStep = {
      timestamp: Date.now(),
      layer,
      step,
      details
    };

    trace.steps.push(stepData);

    // Track message IDs
    if (details.messageId) {
      if (!trace.messageIds.includes(details.messageId)) {
        trace.messageIds.push(details.messageId);
      } else {
        trace.duplicates++;
        console.warn(`[${layer}] ⚠️ TRACE-${traceId} | DUPLICATE MESSAGE ID: ${details.messageId}`);
      }
    }

    // Track optimistic ID
    if (details.optimisticId && !trace.optimisticId) {
      trace.optimisticId = details.optimisticId;
    }

    // Format and log
    const elapsed = Date.now() - trace.startTime;
    console.log(
      `[${layer}] 🔍 TRACE-${traceId} | STEP: ${step} | ${JSON.stringify(details)} | +${elapsed}ms`
    );
  }

  /**
   * Associate a message ID with a trace
   */
  static associateMessageId(traceId: string, messageId: number): void {
    if (!this.enabled || !traceId) {
      return;
    }

    const trace = this.traces.get(traceId);
    if (trace && !trace.messageIds.includes(messageId)) {
      trace.messageIds.push(messageId);
    }
  }

  /**
   * Mark a duplicate was detected
   */
  static markDuplicate(traceId: string, messageId: number): void {
    if (!this.enabled || !traceId) {
      return;
    }

    const trace = this.traces.get(traceId);
    if (trace) {
      trace.duplicates++;
      this.logStep(traceId, 'duplicate-detected', {
        messageId,
        totalDuplicates: trace.duplicates
      });
    }
  }

  /**
   * End a trace and log summary
   */
  static endTrace(traceId: string): void {
    if (!this.enabled || !traceId) {
      return;
    }

    const trace = this.traces.get(traceId);
    if (!trace) {
      return;
    }

    trace.completed = true;
    const duration = Date.now() - trace.startTime;

    const summary = {
      traceId,
      duration: `${duration}ms`,
      messageIds: trace.messageIds,
      finalMessageId: trace.messageIds[trace.messageIds.length - 1],
      duplicates: trace.duplicates,
      steps: trace.steps.length,
      optimisticId: trace.optimisticId
    };

    console.log(`[Frontend] 🔍 TRACE-${traceId} | SUMMARY |`, summary);

    // Log when duplicate deliveries were deduplicated (expected when same confirmation is delivered multiple times)
    if (trace.duplicates > 0) {
      console.warn(
        `[Frontend] TRACE-${traceId} | DUPLICATE DELIVERIES (deduplicated by slice): ${trace.duplicates}`,
        summary
      );
    }

    // Clean up after 1 minute
    setTimeout(() => {
      this.traces.delete(traceId);
    }, 60000);
  }

  /**
   * Get trace data for debugging
   */
  static getTrace(traceId: string): TraceData | undefined {
    return this.traces.get(traceId);
  }

  /**
   * Get all active traces
   */
  static getAllTraces(): TraceData[] {
    return Array.from(this.traces.values());
  }

  /**
   * Clean up old traces
   */
  private static cleanup(): void {
    const now = Date.now();
    const maxAge = 5 * 60 * 1000; // 5 minutes

    for (const [traceId, trace] of Array.from(this.traces.entries())) {
      if (now - trace.startTime > maxAge) {
        this.traces.delete(traceId);
      }
    }
  }

  /**
   * Enable/disable tracing
   */
  static setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /**
   * Check if tracing is enabled
   */
  static isEnabled(): boolean {
    return this.enabled;
  }
}

export default MessageTracer;
